import React, {
  ComponentPropsWithoutRef,
  ElementRef,
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { twMerge } from 'tailwind-merge';

import { L10Common } from 'helpers/l10n/L10nCommon';

import { ReactComponent as DownArrowIcon } from 'images/downArrow.svg';
import { ReactComponent as CheckGreenCircle } from 'images/ic_check_green_circle.svg';
import { ReactComponent as ChevronIcon } from 'images/ic_chevron_right.svg';
import { ReactComponent as CrossIconIcon } from 'images/ic_cross.svg';

import * as Select from '@radix-ui/react-select';
import AvomaTooltip from './AvomaTooltip';
import SearchField from './search/SearchField';

/*
items: TItem[];

type TItem = {
  label: string;
  value: string;
  disabled?: boolean;
};
*/

interface ISelectItemOption {
  label: string;
  value: string;
  disabled?: boolean;
  disabledTooltip?: string;
  tooltipClasses?: string;
  description?: string;
  icon?: ReactNode;
}

interface IAvomaSelectProps {
  placeholder?: string;
  label?: string;
  items: ISelectItemOption[];
  value?: any;
  controlled?: boolean;
  open?: boolean;
  setOpen?: (isOpen: boolean) => void;
  onValueChange?: (value: any) => void;
  closeCleanupHandler?: () => void;
  contentProps?: Record<string, any> &
    React.HTMLAttributes<HTMLDivElement> &
    ComponentPropsWithoutRef<typeof Select.Content>;
  triggerProps?: Record<string, any> &
    React.ButtonHTMLAttributes<HTMLButtonElement> &
    ComponentPropsWithoutRef<typeof Select.Trigger>;
  triggerLabel?: ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  selectItemClassName?: string;
  _key?: string;
  portalContainer?: HTMLElement | null;
  contentPosition?: 'popper' | 'item-aligned';
  disabled?: boolean;
  disabledTooltip?: string;
  labelComponent?: ReactNode;
  selectItemContainerClassName?: string;
  showClearCta?: boolean;
  additionalAction?: ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** When true, omits Radix scroll up/down chevrons; list still scrolls normally. */
  hideScrollButtons?: boolean;
}

interface ISelectItemProps
  extends ComponentPropsWithoutRef<typeof Select.Item> {
  children: ReactNode;
  disabledTooltip?: string;
  tooltipClasses?: string;
  description?: string;
  icon?: ReactNode;
  selectItemClassName?: string;
  selectItemContainerClassName?: string;
}

const AvomaSelect = ({
  placeholder = 'Select',
  label,
  items,
  value,
  controlled,
  open,
  setOpen,
  onValueChange,
  closeCleanupHandler,
  contentProps,
  triggerProps,
  triggerLabel,
  triggerClassName = '',
  contentClassName = '',
  selectItemClassName = '',
  _key, // To reset the select since changing it to undefined doesn't reset it https://github.com/radix-ui/primitives/issues/1569
  portalContainer, // useful for when the select is to be added over another portal like drawer/modal. Pass the parent ref here
  contentPosition = 'popper', // item-aligned works like default macOS select, use 'popper' to make it like a dropdown,
  disabled = false,
  labelComponent, // Useful for adding help component hovers
  selectItemContainerClassName = '',
  showClearCta = false,
  additionalAction, // Useful for adding additional actions inside trigger (e.g. delete item where select is used)
  searchable = false, // Enable search functionality
  searchPlaceholder = 'Search...', // Placeholder for search input
  hideScrollButtons = false
}: IAvomaSelectProps) => {
  const [internalPopoverState, setInternalPopoverState] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const popoverState = controlled ? open : internalPopoverState;
  const setPopoverState = controlled ? setOpen : setInternalPopoverState;

  const handleClearCta = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    (onValueChange as ((value?: string) => void) | undefined)?.(undefined);
    setPopoverState?.(false);
  };

  // Filter items based on search term when searchable is enabled
  // Only show items that match the search term
  const filteredItems = useMemo(() => {
    if (!searchable || !searchTerm) {
      return items;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return items.filter(item =>
      item.label?.toLowerCase().includes(lowerSearchTerm)
    );
  }, [items, searchTerm, searchable]);

  // Find the selected item to check if it's in filtered results
  const selectedItem = useMemo(() => {
    if (!value) return null;
    return items.find(item => item.value === value);
  }, [value, items]);

  // When searchable, compute the label from the original items array so the
  // trigger still shows the selected value even when it is filtered out.
  // When NOT searchable, leave children undefined so Radix's built-in
  // ItemText-portal mechanism renders the selected item text automatically.
  const selectedItemLabel = useMemo(() => {
    if (!searchable) return triggerLabel;
    if (!value) return triggerLabel;
    return selectedItem?.label || triggerLabel;
  }, [value, selectedItem, triggerLabel, searchable]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Clear search when closing
      setSearchTerm('');
      if (closeCleanupHandler && typeof closeCleanupHandler === 'function') {
        closeCleanupHandler();
      }
    }
    setPopoverState?.(isOpen);
  };

  const stopSearchEventPropagation = (
    event:
      | React.KeyboardEvent<HTMLDivElement>
      | React.PointerEvent<HTMLDivElement>
      | React.MouseEvent<HTMLDivElement>
  ) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (!searchable || !popoverState) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      const searchInput = searchContainerRef.current?.querySelector('input');

      if (
        searchInput &&
        document.activeElement instanceof HTMLElement &&
        !searchContainerRef.current?.contains(document.activeElement)
      ) {
        searchInput.focus();
      } else if (searchInput && !document.activeElement) {
        searchInput.focus();
      }
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [popoverState, searchTerm, searchable]);

  return (
    <Select.Root
      open={popoverState}
      value={value}
      onValueChange={nextValue => onValueChange?.(nextValue)}
      onOpenChange={handleOpenChange}
      key={_key}
    >
      <Select.Trigger
        aria-label={label}
        className={twMerge(
          'relative flex w-[200px] cursor-pointer items-center justify-between',
          'rounded border border-gainsboro bg-white py-2 px-3 text-sm text-gunmetal',
          'hover:bg-snow transition-colors duration-200 ease-in-out',
          'data-[disabled]:cursor-not-allowed data-[disabled]:bg-gray-200 data-[placeholder]:text-silver data-[disabled]:opacity-60',
          triggerClassName
        )}
        disabled={disabled}
        {...triggerProps}
      >
        <Select.Value placeholder={placeholder}>
          {selectedItemLabel}
        </Select.Value>
        {showClearCta && value ? (
          <div className='flex'>
            <div className='pointer-events-auto mt-0.5'>
              <Select.Icon>
                <CrossIconIcon
                  className='filter-silverDark ml-3 h-3 w-3'
                  onClick={handleClearCta}
                />
              </Select.Icon>
            </div>

            <Select.Icon>
              <DownArrowIcon
                className={twMerge(
                  'ml-3 flex-shrink-0 h-2 w-2 transition-transform duration-200 ease-in-out',
                  popoverState ? 'rotate-180' : ''
                )}
              />
            </Select.Icon>
          </div>
        ) : (
          <div className='right-2 flex items-center space-x-1'>
            <div className='pointer-events-auto'>{additionalAction}</div>
            <Select.Icon>
              <DownArrowIcon
                className={twMerge(
                  'ml-3 flex-shrink-0 h-2 w-2 transition-transform duration-200 ease-in-out',
                  popoverState ? 'rotate-180' : ''
                )}
              />
            </Select.Icon>
          </div>
        )}
      </Select.Trigger>
      <Select.Portal container={portalContainer}>
        <Select.Content
          position={contentPosition}
          className={twMerge(
            'z-[999999] mt-1 max-h-[400px] w-[var(--radix-select-trigger-width)] min-w-[250px] overflow-y-auto rounded border border-gainsboro bg-white shadow-lg',
            contentClassName
          )}
          {...contentProps}
        >
          {!hideScrollButtons && (
            <Select.ScrollUpButton
              className='flex h-6 cursor-default items-center justify-center bg-white text-silver'
              data-testid='avoma-select-scroll-up'
            >
              <ChevronIcon className='ml-3 h-2 w-4 rotate-[270deg]' />
            </Select.ScrollUpButton>
          )}
          <Select.Viewport className='p-1 pt-0'>
            {searchable && (
              <div
                className='sticky top-0 z-10 bg-white p-2'
                ref={searchContainerRef}
                onKeyDownCapture={stopSearchEventPropagation}
                onPointerDownCapture={stopSearchEventPropagation}
                onMouseDownCapture={stopSearchEventPropagation}
              >
                <SearchField
                  handleSearch={setSearchTerm}
                  handleClear={() => setSearchTerm('')}
                  placeholder={searchPlaceholder}
                  searchTitle={searchTerm}
                  instantSearch
                  focusOnLoad
                  componentName='AvomaSelect'
                  size='default'
                  style={{
                    height: '32px'
                  }}
                  className='w-full'
                  onKeyDown={(event: React.KeyboardEvent) =>
                    event.stopPropagation()
                  }
                />
              </div>
            )}
            <Select.Group>
              {label && (
                <Select.Label className='flex items-center p-2 text-sm font-bold text-gunmetal'>
                  {label} {labelComponent}
                </Select.Label>
              )}

              {filteredItems?.length > 0 &&
                filteredItems.map(item => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                    disabledTooltip={item.disabledTooltip}
                    tooltipClasses={item.tooltipClasses}
                    description={item.description}
                    icon={item.icon}
                    selectItemClassName={selectItemClassName}
                    selectItemContainerClassName={selectItemContainerClassName}
                  >
                    {item.label}
                  </SelectItem>
                ))}
              {filteredItems?.length === 0 && searchable && searchTerm && (
                <div className='flex items-center justify-center py-4'>
                  <span className='text-sm text-silver-dark'>
                    {L10Common.noResultsFound}
                  </span>
                </div>
              )}
            </Select.Group>
          </Select.Viewport>
          {!hideScrollButtons && (
            <Select.ScrollDownButton
              className='flex h-6 cursor-default items-center justify-center bg-white text-silver'
              data-testid='avoma-select-scroll-down'
            >
              <ChevronIcon className='ml-3 h-2 w-2 rotate-90' />
            </Select.ScrollDownButton>
          )}
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

const SelectItem = forwardRef<ElementRef<typeof Select.Item>, ISelectItemProps>(
  (
    {
      children,
      disabledTooltip,
      tooltipClasses,
      description,
      icon,
      selectItemClassName,
      selectItemContainerClassName,
      ...itemProps
    },
    forwardedRef
  ) => (
    <Select.Item
      className={twMerge(
        'flex cursor-pointer select-none items-center rounded bg-transparent py-2 px-3 text-sm text-gunmetal data-[disabled]:cursor-not-allowed data-[highlighted]:bg-snow data-[disabled]:opacity-40',
        selectItemContainerClassName
      )}
      {...itemProps}
      ref={forwardedRef}
    >
      <AvomaTooltip
        tooltip={itemProps.disabled && disabledTooltip}
        className={tooltipClasses ?? ''}
      >
        <div className={twMerge('flex  gap-4', selectItemClassName)}>
          {icon}
          <div className='flex flex-col'>
            <Select.ItemText>{children}</Select.ItemText>
            <span className='text-silver'>{description}</span>
          </div>
        </div>
      </AvomaTooltip>
      <Select.ItemIndicator className='absolute right-0 inline-flex w-6 items-center'>
        <CheckGreenCircle className='h-4 w-4' />
      </Select.ItemIndicator>
    </Select.Item>
  )
);

SelectItem.displayName = 'SelectItem';

export default AvomaSelect;
